/**
 * TypeScript types for Configurable Insights feature
 */

// ============================================================================
// Enums
// ============================================================================

export enum ConfigurableInsightLayoutType {
  FLAG_TRANSACTION_ONLY = 'FLAG_TRANSACTION_ONLY',
  FLAG_BALANCE_ONLY = 'FLAG_BALANCE_ONLY',
  FLAG_COMBINED = 'FLAG_COMBINED',
}

export enum InsightConditionType {
  TRANSACTIONS = 'TRANSACTIONS',
  DAILY_BALANCE = 'DAILY_BALANCE',
}

export enum TransactionConditionOperator {
  ANY = 'ANY',
  GREATER_THAN = 'GREATER_THAN',
  LESS_THAN = 'LESS_THAN',
}

export enum DailyBalanceConditionOperator {
  MINIMUM = 'MINIMUM',
  MAXIMUM = 'MAXIMUM',
  AVERAGE = 'AVERAGE',
}

export enum OperatorValueType {
  ABSOLUTE = 'ABSOLUTE',
  PERCENTAGE = 'PERCENTAGE',
}

export enum AmountFieldFilterOperator {
  GREATER_THAN = 'GREATER_THAN',
  LESS_THAN = 'LESS_THAN',
  EQUAL_TO = 'EQUAL_TO',
  BETWEEN = 'BETWEEN',
}

export enum TransactionBaseFieldFilterOperator {
  IS_ONE_OF = 'IS_ONE_OF',
  IS_NOT_ONE_OF = 'IS_NOT_ONE_OF',
}

export enum TimeFieldFrequencyType {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  FORTNIGHTLY = 'FORTNIGHTLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum TimeframeMode {
  SLIDING_WINDOW = 'SLIDING_WINDOW',
  MOST_RECENT_ACCOUNT_PERIOD = 'MOST_RECENT_ACCOUNT_PERIOD',
}

export enum BacktestStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  FINISHED = 'FINISHED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
}

// ============================================================================
// Filter Types
// ============================================================================

/**
 * Amount filter for numeric comparisons
 * - BETWEEN operator requires exactly 2 values [min, max]
 * - Other operators require exactly 1 value
 */
export interface AmountFilter {
  operator: AmountFieldFilterOperator;
  values: number[];
}

/**
 * Base field filter for categorical fields (category, method, merchant, payment_processor)
 * Supports multiple values for IN/NOT_IN operations
 */
export interface BaseFieldFilter {
  operator: TransactionBaseFieldFilterOperator;
  values: (string | number)[];
}

/**
 * Timeframe filter for temporal constraints
 * Specifies time_period_in_days and mode
 */
export interface TimeframeFilter {
  time_period_in_days?: number | null;
  mode: TimeframeMode;
}

/**
 * Transaction filters - all fields are optional
 * Apply multiple filters to narrow down transaction selection
 */
export interface InsightTransactionFilters {
  category?: BaseFieldFilter | null;
  method?: BaseFieldFilter | null;
  merchant?: BaseFieldFilter | null;
  payment_processor?: BaseFieldFilter | null;
  amount?: AmountFilter | null;
  timeframe?: TimeframeFilter | null;
}

/**
 * Daily balance filters - all fields are optional
 * Can filter by amount, timeframe, and volatility
 */
export interface InsightDailyBalanceFilters {
  amount?: AmountFilter | null;
  timeframe?: TimeframeFilter | null;
  volatility?: AmountFilter | null;
}

// ============================================================================
// Condition Types
// ============================================================================

/**
 * Base condition interface with common fields
 */
interface BaseCondition {
  condition_type: InsightConditionType;
  operator: string;
  operator_value_type?: OperatorValueType | null;
  operator_value?: number | null;
}

/**
 * Transaction condition
 * - If operator is ANY: operator_value_type and operator_value must be null
 * - Otherwise: operator_value_type and operator_value are required
 */
export interface InsightTransactionCondition extends BaseCondition {
  condition_type: InsightConditionType.TRANSACTIONS;
  operator: TransactionConditionOperator;
  operator_value_type?: OperatorValueType | null;
  operator_value?: number | null;
  filters: InsightTransactionFilters;
}

/**
 * Daily balance condition
 * - operator_value_type is always ABSOLUTE
 * - operator_value is always null
 */
export interface InsightDailyBalanceCondition extends BaseCondition {
  condition_type: InsightConditionType.DAILY_BALANCE;
  operator: DailyBalanceConditionOperator;
  operator_value_type: OperatorValueType.ABSOLUTE;
  operator_value: null;
  filters: InsightDailyBalanceFilters;
}

/**
 * Union type for all condition types
 */
export type InsightCondition =
  | InsightTransactionCondition
  | InsightDailyBalanceCondition;

// ============================================================================
// Signal Configuration
// ============================================================================

/**
 * Configuration object containing signal settings and logic
 */
export interface InsightConfiguration {
  enabled: boolean;
  impacts_risk: boolean;
  description?: string;
  conditions: InsightCondition[];
}

/**
 * Individual signal (matches backend API format)
 * Contains signal_name at top level and configuration nested
 */
export interface ConfigurableInsight {
  uuid?: string; // Unique identifier from backend
  signal_name: string; // Machine-readable name
  configuration: InsightConfiguration;
  limitations?: string | null;
}

// ============================================================================
// Filter Options & Enrichment Values
// ============================================================================

/**
 * Filter option for dropdowns and multi-selects
 */
export interface FilterOption {
  text: string;
  value: string;
}

/**
 * API response format for enrichment values endpoint
 */
export interface EnrichmentValuesApiResponse {
  categories: string[];
  methods: string[];
  payment_processors?: string[];
}

/**
 * Internal format for enrichment values after transformation
 */
export interface EnrichmentValues {
  transaction_categories: FilterOption[];
  transaction_methods: FilterOption[];
  payment_processors: FilterOption[];
}

/**
 * Response from POST /api/v2/backtests/
 */
export interface BacktestJob {
  backtest_uuid: string;
  signal_uuid: string | null;
  status: BacktestStatus;
}

/**
 * Backtest result returned by /api/v2/backtests/{uuid}/progress
 */
export interface BacktestResult {
  backtest_uuid: string;
  signal_uuid: string | null;
  status: BacktestStatus;
  progress: number;
  finished_count: number;
  cohort_size: number;
  flagged_count: number;
  flag_rate: number;
}
